import { Capacitor } from '@capacitor/core';

/**
 * Feature flag — install promotion (PWA Add-to-Home / Capacitor app
 * download). Defaults OFF until the native app is published to the
 * stores; with the flag off, both the first-recording gate modal and
 * the passive PwaPrompts toast skip their offers entirely. The
 * `beforeinstallprompt` capture below stays wired so flipping the flag
 * back to true (via `VITE_INSTALL_PROMO=true` in `.env`) re-enables
 * everything without a code change.
 *
 * Why a flag and not a delete: we WILL ship native apps eventually and
 * the recording-gate UX is genuinely better than a passive toast for
 * mobile-web users — preserving the wiring keeps that re-enable cheap.
 */
const INSTALL_PROMO_ENABLED =
  (import.meta.env?.VITE_INSTALL_PROMO ?? 'false').toString().toLowerCase() ===
  'true';

/**
 * Single source of truth for the captured `beforeinstallprompt` event.
 * The browser only fires it ONCE per page load, so we have to cache it
 * the first time the listener fires and let any UI surface that wants
 * to trigger an install (passive PwaPrompts toast OR the first-recording
 * gate modal) read it from here. Multiple competing listeners that each
 * stash their own ref would silently lose to whoever wired up first.
 *
 * Lifetime: cleared on `appinstalled`, on `prompt() → outcome`, or on
 * page navigation. A second `beforeinstallprompt` overwrites freely.
 */

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const subscribers = new Set<() => void>();

const notify = () => {
  for (const cb of subscribers) cb();
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });
}

export const getDeferredInstallPrompt = (): BeforeInstallPromptEvent | null =>
  deferredPrompt;

export const hasDeferredInstallPrompt = (): boolean => deferredPrompt !== null;

/**
 * Subscribe to availability changes. Returns the unsubscribe fn. Used by
 * React components that need to re-render when the prompt becomes
 * available (the event fires asynchronously after the page loads).
 */
export const subscribeInstallPrompt = (cb: () => void): (() => void) => {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
};

/**
 * Trigger the cached prompt. Returns the user's choice, or null if no
 * prompt is currently cached (caller should fall back to instructions
 * for iOS Safari etc., where `beforeinstallprompt` never fires).
 */
export const triggerInstallPrompt = async (): Promise<
  'accepted' | 'dismissed' | null
> => {
  const prompt = deferredPrompt;
  if (!prompt) return null;
  try {
    await prompt.prompt();
    const choice = await prompt.userChoice;
    deferredPrompt = null;
    notify();
    return choice.outcome;
  } catch {
    deferredPrompt = null;
    notify();
    return null;
  }
};

// ──────────────────────────────────────────────────────────────────────
// Install context detection
// ──────────────────────────────────────────────────────────────────────

/** True when the page is running as an installed PWA (added-to-home, standalone). */
export const isStandaloneDisplay = (): boolean => {
  if (typeof window === 'undefined') return false;
  // matchMedia is the modern signal; navigator.standalone is the iOS Safari fallback.
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
};

/** True inside a Capacitor native shell (iOS/Android wrapper). */
export const isCapacitorNative = (): boolean => Capacitor.isNativePlatform();

/** True when the app is already installed in some form (PWA OR native). */
export const isInstalled = (): boolean =>
  isStandaloneDisplay() || isCapacitorNative();

/**
 * Touch-device + small-or-tablet viewport. iPad on iPadOS reports as Mac
 * in the UA, so we lean on `navigator.maxTouchPoints` (which iPadOS
 * reports >1) rather than parsing UA strings.
 */
export const isMobileFormFactor = (): boolean => {
  if (typeof window === 'undefined') return false;
  const touch =
    (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1) ||
    'ontouchstart' in window;
  if (!touch) return false;
  // Cap at iPad-Pro-portrait so a touchscreen laptop doesn't get
  // false-positived as "should install".
  return window.matchMedia?.('(max-width: 1024px)').matches ?? true;
};

/** True only on iOS Safari, where `beforeinstallprompt` never fires. */
export const isIOSSafari = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  if (isCapacitorNative()) return false;
  const ua = navigator.userAgent;
  const isIPhone = /iPhone|iPod/.test(ua);
  // iPadOS reports as Mac UA — combine with touch hint to catch it.
  const isIPadOS =
    /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  if (!isIPhone && !isIPadOS) return false;
  // Exclude in-app browsers (Instagram, Facebook etc.) that can't install.
  return !/CriOS|FxiOS|EdgiOS|GSA/.test(ua);
};

// ──────────────────────────────────────────────────────────────────────
// "First mobile-web recording" gate persistence
// ──────────────────────────────────────────────────────────────────────

const NUDGE_KEY = 'maie.install.recordingGateShownAt';

/**
 * Should we interrupt the doctor's first recording attempt with an
 * install nudge? Yes only when:
 *   - we're on a mobile/tablet form factor,
 *   - we're NOT already installed,
 *   - we have not nudged before in this browser.
 *
 * Desktop always returns false — the QR flow exists for desktop-no-mic
 * cases and isn't gated through here.
 */
export const shouldNudgeInstallOnRecord = (): boolean => {
  // Hard-off until the native app ships. The modal would point doctors
  // at "Install MAIE" / "Add to Home Screen" affordances that lead to
  // a PWA we haven't promoted as an app yet — confusing in a clinical
  // setting and (per recent field reports) blocking the first recording
  // tap on mobile-web. Flip VITE_INSTALL_PROMO=true to restore.
  if (!INSTALL_PROMO_ENABLED) return false;
  if (isInstalled()) return false;
  if (!isMobileFormFactor()) return false;
  try {
    return localStorage.getItem(NUDGE_KEY) === null;
  } catch {
    return true;
  }
};

/** Public predicate so the passive PWA toast can skip in lockstep. */
export const isInstallPromotionEnabled = (): boolean => INSTALL_PROMO_ENABLED;

export const markInstallNudgeShown = (): void => {
  try {
    localStorage.setItem(NUDGE_KEY, String(Date.now()));
  } catch {
    /* storage unavailable — re-nudge next session, not fatal */
  }
};
